import { Component, OnInit, AfterViewInit, HostListener} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

@Component({
  selector: 'app-floating-bar',
  templateUrl: './floating-bar.component.html',
  styleUrls: ['./floating-bar.component.scss']
})
export class FloatingBarComponent implements OnInit, AfterViewInit {
  isScrolled = false;
  floatingOpen = false;
  isMobile = false;
  isHomePage = false;
  isExampleModalVisible = false;
  isNpfModalVisible = false;
  private hasViewInitialized = false;
  private npfScriptsLoaded = false;
  private npfScriptsLoading = false;

  constructor(private router: Router) { }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.isScrolled = window.scrollY > 200;
  }

  @HostListener('window:resize', [])
  onResize() {
    this.isMobile = window.innerWidth <= 768;
    if (!this.isMobile) {
      this.floatingOpen = false;
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    const container = document.querySelector('.sticky-bar') as HTMLElement;
    if (!container) return;
    if (container.contains(target)) return;
    if (this.floatingOpen) {
      this.floatingOpen = false;
    }
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth <= 768;
    this.updateHomePageState(this.router.url);
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    ).subscribe(event => {
      this.updateHomePageState(event.urlAfterRedirects);
      if (this.hasViewInitialized && this.isHomePage) {
        this.showExampleModalIfPresent();
      }
    });

    document.addEventListener('readystatechange', _ => {
      $(".menu-item-text").addClass("page-load-hover");
    });
  }

  private updateHomePageState(url: string) {
    const path = url.split('?')[0].split('#')[0];
    this.isHomePage = path === '/' || path === '';
  }

  private loadNpfWidgetScripts(): Promise<void> {
    if (this.npfScriptsLoaded) {
      return Promise.resolve();
    }

    if (this.npfScriptsLoading) {
      return new Promise(resolve => {
        const check = setInterval(() => {
          if (this.npfScriptsLoaded) {
            clearInterval(check);
            resolve();
          }
        }, 100);
      });
    }

    this.npfScriptsLoading = true;

    const scriptSources = [
      'https://widgets.nopaperforms.com/emwgts.js',
      'https://track.nopaperforms.com/js/track.js',
      'https://chatbot.in1.nopaperforms.com/en-gb/backend/bots/niaachtbtscpt.js/4885f35478c51848/9d4303fa6f5d402cb4dcd0e0b7c88f29'
    ];

    const loadOne = (src: string): Promise<void> => {
      return new Promise(resolve => {
        const s = document.createElement('script');
        s.type = 'text/javascript';
        s.async = true;
        s.src = src;
        s.onload = () => resolve();
        s.onerror = (e) => {
          console.warn(`nopaperforms script failed to load: ${src}`, e);
          resolve();
        };
        document.body.appendChild(s);
      });
    };

    return Promise.all(scriptSources.map(loadOne)).then(() => {
      this.npfScriptsLoaded = true;
      this.npfScriptsLoading = false;
    });
  }

  private showExampleModalIfPresent(retriesLeft: number = 10): void {
    const modalEl = document.getElementById('exampleModal');

    if (!modalEl) {
      if (retriesLeft > 0) {
        setTimeout(() => this.showExampleModalIfPresent(retriesLeft - 1), 100);
      }
      return;
    }

    const modalInstance = (window as any).bootstrap?.Modal?.getOrCreateInstance?.(modalEl);
    if (modalInstance) {
      modalInstance.show();
    } else {
      modalEl.classList.add('show');
      modalEl.style.display = 'block';
      modalEl.removeAttribute('aria-hidden');
      modalEl.setAttribute('aria-modal', 'true');
    }
  }

  ngAfterViewInit() {
    this.hasViewInitialized = true;

    const modalEl = document.getElementById('myModal') as HTMLElement | null;
    const btn = document.getElementById('myBtn') as HTMLElement | null;
    const closeEl = modalEl?.querySelector('.close') as HTMLElement | null;
    const bootstrapModal = (window as any).bootstrap?.Modal;
    let modalInstance: any = null;

    if (modalEl && bootstrapModal) {
      modalInstance = new bootstrapModal(modalEl, { backdrop: 'static', keyboard: false });
    }

    btn?.addEventListener('click', () => {
      if (modalInstance) {
        modalInstance.show();
      } else if (modalEl) {
        modalEl.style.display = 'block';
        modalEl.classList.add('show');
        modalEl.removeAttribute('aria-hidden');
        modalEl.setAttribute('aria-modal', 'true');
      }
      document.body.style.overflow = 'hidden';
      this.loadNpfWidgetScripts();
    });

    closeEl?.addEventListener('click', () => {
      if (modalInstance) {
        modalInstance.hide();
      } else if (modalEl) {
        modalEl.style.display = 'none';
        modalEl.classList.remove('show');
        modalEl.setAttribute('aria-hidden', 'true');
      }
      document.body.style.overflow = '';
    });

    if (this.isHomePage) {
      this.showExampleModalIfPresent();
    }

    // Load Nia chat scripts and reveal the widget automatically —
    // no click needed. Runs once after the view is ready.
    this.loadNpfWidgetScripts().then(() => {
      this.waitForChatWidget();
    });
  }

  private waitForChatWidget(retriesLeft: number = 30): void {
    const chatEl = document.querySelector('.npf_chatbots') as HTMLElement | null;

    if (!chatEl) {
      if (retriesLeft > 0) {
        setTimeout(() => this.waitForChatWidget(retriesLeft - 1), 200);
      } else {
        console.warn('Nia chat widget element (.npf_chatbots) never appeared after script load.');
      }
      return;
    }

    chatEl.style.display = 'block';
    chatEl.style.opacity = '1';
    chatEl.style.visibility = 'visible';
    chatEl.style.zIndex = '99999';
  }

  toggleFloating(event: Event) {
    event.stopPropagation();
    this.floatingOpen = !this.floatingOpen;
  }

  // Kept for backward compatibility if any template still calls this on click —
  // it simply re-runs the same auto-show logic.
  openNiaChat(event: Event) {
    event.preventDefault();
    this.loadNpfWidgetScripts().then(() => {
      this.waitForChatWidget();
    });
  }
}
